import { ClampToEdgeWrapping, Color, DoubleSide,  GreaterDepth, MeshBasicMaterial, NearestFilter, TextureLoader } from 'three';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { newGuid } from '../../hsbcad-model';
import EdgeShader from '../Shaders/EdgeShader'
import * as THREE from 'three'
import { LineMaterial, LineMaterialParameters } from 'three/examples/jsm/lines/LineMaterial';
import { Viewer } from '../Viewer';

type MaterialTypes =
    THREE.MeshStandardMaterial | THREE.LineBasicMaterial | THREE.LineDashedMaterial | THREE.MeshLambertMaterial | THREE.MeshBasicMaterial | THREE.MeshPhongMaterial | THREE.ShaderMaterial;
type MaterialClasses<T> =
    T extends THREE.MeshStandardMaterial ? THREE.MeshStandardMaterial :
    T extends THREE.LineBasicMaterial ? THREE.LineBasicMaterial :
    T extends THREE.LineDashedMaterial ? THREE.LineDashedMaterial :
    T extends THREE.MeshLambertMaterial ? THREE.MeshLambertMaterial :
    T extends THREE.MeshPhongMaterial ? THREE.MeshPhongMaterial :
    T extends THREE.MeshBasicMaterial ? THREE.MeshBasicMaterial :
    T extends LineMaterial ? LineMaterial :
    T extends THREE.ShaderMaterial ? THREE.ShaderMaterial :
    never;
type MaterialParams<T> =
    T extends THREE.MeshStandardMaterial ? THREE.MeshStandardMaterialParameters :
    T extends THREE.LineDashedMaterial ? THREE.LineDashedMaterialParameters :
    T extends THREE.LineBasicMaterial ? THREE.LineBasicMaterialParameters :
    T extends THREE.MeshLambertMaterial ? THREE.MeshLambertMaterialParameters :
    T extends THREE.MeshPhongMaterial ? THREE.MeshPhongMaterialParameters :
    T extends THREE.MeshBasicMaterial ? THREE.MeshBasicMaterialParameters :
    T extends LineMaterial ? LineMaterialParameters :
    T extends THREE.ShaderMaterial ? THREE.ShaderMaterialParameters :
    never;

export class MaterialPresets<T extends MaterialTypes = THREE.MeshStandardMaterial> {
    static readonly BEAM = new MaterialPresets('BEAM', { color: '#ff9900' });
    static readonly SHEET = new MaterialPresets('BEAM', { color: '#8F4700' });
    static readonly UNITTABLE = new MaterialPresets('UNITTABLE', { color: '#9191af', emissive: '#9191af', metalness: 1 });
    static readonly METAL = new MaterialPresets('METAL', { color: '#9191af', emissive: '#9191af', metalness: 1 });
    static readonly UNITSAW = new MaterialPresets('UNITSAW', { color: '#006600', emissive: '#006600', metalness: 1 });
    static readonly PANEL = new MaterialPresets('PANEL', { color: '#FF4D4D', emissive: '#FF4D4D', metalness: 0 });
    static readonly WINDOW = new MaterialPresets('WINDOW', { color: '#82EBF1', opacity: 0.5 });
    static readonly LINE = new MaterialPresets<THREE.LineBasicMaterial>('LINE', { color: 0x000000 });
    static readonly DOTTEDLINE = new MaterialPresets<THREE.LineDashedMaterial>('DOTTEDLINE', { color: 0x000000, dashSize: 30, gapSize: 10 });
    // private to disallow creating other instances of this type
    private constructor(private readonly key: string, public readonly value: Partial<MaterialParams<T>>) {
    }

    toString() {
        return this.key;
    }

}
export class MaterialManager {

    private _panelbeammaterial!: THREE.MeshStandardMaterial;
    private _panelsheetmaterial!: MaterialTypes;
    private _nonhsbmaterial!: MaterialTypes;
    private _metalpartmaterial!: MaterialTypes;
    private _hidematerial!: THREE.MeshStandardMaterial;
    private _materials: { [key: string]: MaterialTypes };
    static _japaneseFont;
    private _selectmaterial!: THREE.MeshStandardMaterial;
    private _highlightmaterial!: THREE.MeshStandardMaterial;
    private _redmaterial!: THREE.MeshStandardMaterial;
    private _greenmaterial;
    private _panelbodymaterial;
    private _ghostmaterial;
    private _blackmaterial;
    private _blackMaterialNoShine;
    private _cyanmaterial;
    private _yellowmaterial;
    private _greymaterial;
    private _whitematerial;
    private _lineMaterial!: LineMaterial;
    private _lineBasicMaterial!: THREE.LineBasicMaterial;
    private _dottedLineMaterial;
    private _wireframeMaterial;

    private _windowmaterial;


    constructor(private _viewer: Viewer) {
        this._materials = {};
        this.loadMaterials()
        MaterialManager.loadFont();
    }

    cleanUp() {
        Object.keys(this._materials).forEach((key: string) => {
            this._materials[key]?.dispose();
        })
        this._materials = {};
        let properties = Object.getOwnPropertyNames(this).filter((p) => p.toLowerCase().includes('material') && p !== '_materials');
        properties.forEach((p)=>{
            this[p].dispose();
            delete this[p];
        })
    }

    static loadFont() {
        var fontLoader = new FontLoader();

        // if (!MaterialManager._japaneseFont) {
        //     fontLoader.load("assets/fonts/JapaneseLight.json", function (font) {
        //         MaterialManager._japaneseFont = font;
        //     });
        // }
    }
    static setFont(fontData) {
        var fontLoader = new FontLoader();

        if (!MaterialManager._japaneseFont) {
            MaterialManager._japaneseFont = fontLoader.parse(fontData);
        }
    }
    loadMaterials() {
        let _self = this;

        // Clear the existing materials table.
        this._metalpartmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#9191af', emissive: '#9191af', metalness: 1 });

        this._selectmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#7094FF' });
        this._selectmaterial.opacity = 0.75;
        this._selectmaterial.emissive = this._selectmaterial.color;
        this._selectmaterial.depthWrite = true;

        this._highlightmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#fbff70', emissive: '#fbff70', metalness: 1 });
        this._highlightmaterial.emissive = this._highlightmaterial.color;
        this._highlightmaterial.depthWrite = true;

        this._hidematerial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#f0f0f4', emissive: '#f0f0f4', metalness: 1, opacity: 0.025 });
        this._hidematerial.visible = false;


        this._panelbeammaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#ffffff', emissive: '#0f0f0f' });

        this._redmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#ff0000' });

        this._greenmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#33cc33' });

        this._blackmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#000000', metalness: 1 });


        this._blackMaterialNoShine = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#000000' });

        this._cyanmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#66ffff' });

        this._yellowmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#ffff00' });

        this._nonhsbmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#7d7d5c', opacity: 0.5 });

        this._panelsheetmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#8F4700' });

        this._panelbodymaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#007D64' });

        this._ghostmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#888888', opacity: 0.3 });

        this._greymaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#f0f0f5', opacity: 0.5 });

        this._whitematerial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#ffffff' });

        this._wireframeMaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#000000', wireframe: true });

        this._lineMaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), {
            color: 0x010101,
            dashed: false,
            linewidth: 2,
            opacity: 0.2,
            transparent: true,
            alphaTest: 0.01
        }, LineMaterial);
        this._lineMaterial.resolution.set(this._viewer._container.clientWidth / 2, this._viewer._container.clientHeight / 2);

        this._lineBasicMaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#000000', linewidth: 2 }, THREE.LineBasicMaterial);

        this._dottedLineMaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: 0x000000, dashSize: 30, gapSize: 10 }, THREE.LineDashedMaterial);

        this._windowmaterial = _self.addNewMaterial('Hsb-Material-' + newGuid(), { color: '#82EBF1', opacity: 0.5 });

        return true;
    };

    getBeamMaterial() {

        return this._panelbeammaterial;
    }

    getSheetMaterial() {

        return this._panelsheetmaterial;
    }

    getPanelBodyMaterial() {

        return this._panelbodymaterial;
    }

    getWhiteMaterial() {

        return this._whitematerial;
    }

    getSelectMaterial() {

        return this._selectmaterial;
    }

    getHighlightMaterial() {

        return this._highlightmaterial;
    }

    getNonHsbMaterial() {

        return this._nonhsbmaterial;
    }

    getGreenmaterial() {
        return this._greenmaterial;
    }
    getBlackmaterial() {
        return this._blackmaterial;
    }
    getBlackmaterialNoShine() {
        return this._blackMaterialNoShine;
    }
    getMetalpartmaterial() {

        return this._metalpartmaterial;
    }

    getHidematerial() {

        return this._hidematerial;
    }

    getGhostmaterial() {

        return this._ghostmaterial;
    }

    getGreymaterial() {

        return this._greymaterial;
    }
    getCyanmaterial() {

        return this._cyanmaterial;
    }
    getYellowmaterial() {

        return this._yellowmaterial;
    }
    getRedmaterial() {

        return this._redmaterial;
    }

    getLinematerial(): LineMaterial {
        return this._lineMaterial;
    }

    getLineBasicmaterial(): THREE.LineBasicMaterial {
        return this._lineBasicMaterial;
    }

    getDottedLinematerial(): THREE.LineDashedMaterial {
        return this._dottedLineMaterial;
    }

    getWireFrameMaterial() {
        return this._wireframeMaterial;
    }

    getHelvetikerfont() {
        return MaterialManager._japaneseFont;
    }

    getJapaneseFont() {
        //return this._japaneseFont;
    }

    addTextMaterial(texture, materialId, callback) {
        let _self = this;
        const loader = new TextureLoader();
        loader.load(texture, function (tex) {
            //tex.wrapS  = RepeatWrapping;
            //tex.wrapT = RepeatWrapping;
            tex.wrapS = ClampToEdgeWrapping;
            tex.wrapT = ClampToEdgeWrapping;
            tex.minFilter = NearestFilter;

            var material = new MeshBasicMaterial({
                map: tex,
                side: DoubleSide
            });

            if (!_self._materials) {
                _self._materials = {};
            }
            material.userData.materialHsbId = materialId;
            _self._materials[materialId] = material;

            if (callback) {
                callback(material);
            }

        });

        return;

    }

    /**
     * Creates a new material for the given params and adds it into the material hashtable.
     * @param materialId A unique ID for this material.
     * @param params Depending on the given type the proper params to fill in.
     * @param type The desired materialtype. By default THREE.MeshStandardMaterial
     * @returns The newly created material.
     */
    addNewMaterial<T extends MaterialTypes | LineMaterial = THREE.MeshStandardMaterial>(materialId, params?: Partial<MaterialParams<T>>, type?: new (params: Partial<MaterialParams<T>>) => T): MaterialClasses<T> {
        let parameters: any = params ?? {}
        var material: any;
        if (type)
            material = new type(params!);
        else
            material = new THREE.MeshStandardMaterial(params);

        if (parameters.color)
            material.color = new THREE.Color(parameters.color);
        if (parameters.emissive)
            material.emissive = new THREE.Color(parameters.emissive);
        if (parameters.metal)
            material.metalness = parameters.metal;
        if (parameters.opacity) {
            material.transparent = true;
            material.opacity = parameters.opacity;
        }
        if (parameters.side)
            material.metalness = parameters.side;
        if (!this._materials)
            this._materials = {};
        if (parameters.wireframe) {
            material.color = new THREE.Color(0x0)
            material.polygonOffset = true;
            material.polygonOffsetFactor = -1;
            material.polygonOffsetUnits = -4;
            material.opacity = 0.9;
            material.wireframeLinewidth = 5;
            material.transparent = true;
            material.wireframe = true;
        }
        material.userData.materialHsbId = materialId;
        this._materials[materialId] = material;

        material.side = THREE.DoubleSide;
        material.flatShading = true;
        return material;
    }
    /**
     * Returns a material that fits the given preset and personal params for the entity.
     * @param {MaterialPresets} preset The desired preset values for the material
     * @param {MaterialParams<T>} params Params that are presonal to this entity
     * @returns Either a material that was found with the proper params or a newly created material
     */
    getPresetMaterial<T extends MaterialTypes = THREE.MeshStandardMaterial>(preset: MaterialPresets, params?: MaterialParams<T>) {
        let presetParams = preset.value;
        if (params) {
            Object.keys(params).forEach(key => params[key] === undefined ? delete params[key] : {}); // Get rid of undefined params
            Object.assign(presetParams, params);
        }
        if (presetParams.opacity && presetParams.opacity < 1)
            presetParams.transparent = true;
        let hitMaterial = Object.values(this._materials).find((mat => {
            return Object.entries(presetParams).every(([key, value]) => {
                switch (key) {
                    case 'color': {
                        return "#" + mat[key]?.getHexString().toLowerCase() === value;
                    }
                    case 'opacity': {
                        return (mat[key] === value || (!mat[key] && value === 1) || (mat[key] === 1 && !value))
                    }
                    default: {
                        if (typeof (value) === 'number') {
                            if (typeof (mat[key]) !== 'number')
                                return false;
                            return Math.abs(mat[key] - value) < 0.01;
                        }
                        return mat[key] === value;
                    }
                }
            });
        }))
        if (!hitMaterial) {
            hitMaterial = this.addNewMaterial('Hsb-Material-' + newGuid(), presetParams);
        }
        return hitMaterial.clone();
    }
    /**
     * Adds the given material into the hashtable of this manager.
     * @param material The material to be added
     * @param materialId The ID to be given.
     * @returns 
     */
    addMaterial(material: MaterialTypes, materialId) {
        if (!this._materials) {
            this._materials = {};
        }
        this._materials[materialId] = material;

        material.userData.materialHsbId = materialId;

        return material;
    }

    /**
     * Sets clipping planes for all materials
     * @param planes The desired clipping planes to be set. If null will disable clipping planes.
     */
    setClippingPlaneToAllMaterials(planes) {

        let self = this;

        Object.keys(self._materials).forEach(function (key) {

            let material = self._materials[key];

            material.clippingPlanes = planes;
            material.clipIntersection = planes ? true : false;

        });

    }

    /**
     * Returns the material with the given id, null if none was found.
     * @param materialId 
     * @returns 
     */
    findMaterial(materialId) {

        if (this._materials) {
            return this._materials[materialId];
        }

        return null;
    }

    getGhostMaterial() {
        return this._ghostmaterial;
    }
    getWindowMaterial() {

        return this._windowmaterial;
    }

    /**
     * Returns ref to material for the given colors if present, adds the material if not yet present.
     * @param {string | Color} color The color for which to check if there is a material (if string, should be a hex string).
     */
    getobjectMaterial(color: string | Color, opacity?: number) {
        let searchColor = (typeof (color) === 'string') ? new THREE.Color(color) : color;
        let hitMaterial = Object.values(this._materials)
            .filter((mat) => mat) // only defined values
            .find((material: any) => { return searchColor === material.color && (!opacity ? (!material.transparent || material.opacity === 1) : (material.transparent && material.opacity === opacity)) }); // get material with correct color and opacity if given
        if (!hitMaterial) {
            hitMaterial = this.addNewMaterial('Hsb-Material-' + newGuid(), { color: color, opacity: opacity });
        }
        return hitMaterial;
    }
}
